/**
 * Step, Connect to your 3PL (BLU-661 client variant; layout B, BLU-1376).
 * Wraps the existing ClaimSharedLocationSheet as a wizard step (G6: the sheet
 * is not restyled; G5: approval logic is not touched): the operator enters
 * the claim code their 3PL shared, binding one of their Shopify locations to
 * the partner warehouse. Completion is server-driven: the org has at least one
 * active `location_client_organizations` junction (`activeJunctionCount`).
 *
 * The sheet is a controlled Sheet with no trigger of its own, mounted as a
 * sibling of the pane, opened by the pane's primary button. Its internal
 * Shopify-locations query only fires on the sheet's step 2, so keeping it
 * mounted is cheap.
 *
 * A submitted claim sits in `pending_approval` until the 3PL approves. It does
 * not count as done, but the step model reads it as Waiting (handover 4.2),
 * which opens the next step, and the body shows the amber waiting note
 * instead of the empty prompt so the operator does not redeem again (DESIGN
 * 13.1 pending_approval amber).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Link2 } from 'lucide-react';
import { PaneActionButton } from '../shell/PaneActionButton';
import ClaimSharedLocationSheet from '@/features/locations/components/ClaimSharedLocationSheet';
import { StepPane } from '../shell/StepPane';
import { DeepLinkStat } from './DeepLink';

interface StepClaimProps {
  /** Active client junctions (getOnboardingStatus.activeJunctionCount) — drives completion. */
  junctionCount: number;
  /**
   * Submitted-but-unapproved client junctions (getOnboardingStatus.pendingJunctionCount).
   * Shows the "awaiting approval" state; never counts toward completion.
   */
  pendingCount: number;
  /** Refetch the onboarding status after a claim is submitted / approved. */
  onClaimed: () => void;
}

export function StepClaim({ junctionCount, pendingCount, onClaimed }: StepClaimProps) {
  const { t } = useTranslation('onboarding');
  const [open, setOpen] = useState(false);

  const claimButton = (
    <PaneActionButton type="button" onClick={() => setOpen(true)}>
      <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
      {t('stepClaim.claim')}
    </PaneActionButton>
  );

  return (
    <>
      <StepPane
        title={t('stepClaim.title')}
        description={t('stepClaim.description')}
        primary={claimButton}
        // Only a live connection is a result worth keeping; a pending claim
        // says so in the body instead.
        summary={junctionCount > 0 ? t('stepClaim.summary', { count: junctionCount }) : undefined}
      >
        <DeepLinkStat value={junctionCount} label={t('stepClaim.connections', { count: junctionCount })} />
        {junctionCount === 0 &&
          (pendingCount > 0 ? (
            <div
              role="status"
              className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900"
            >
              <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t('stepClaim.pendingNote')}</span>
            </div>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">{t('stepClaim.emptyNote')}</p>
          ))}
      </StepPane>

      <ClaimSharedLocationSheet open={open} onOpenChange={setOpen} onComplete={onClaimed} />
    </>
  );
}
